<script setup>
import { ref, computed, nextTick } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useToast } from 'vue-toast-notification';
import http from '@/services/http';
import html2canvas from 'html2canvas-pro';

const route = useRoute();
const store = useStore();
const toast = useToast();

const isCapturing = ref(false);
const isModalOpen = ref(false);
const isSubmitting = ref(false);
const showImagePreview = ref(false);

const comment = ref('');
const capturedScreenshot = ref(null);
const captureMethod = ref('none'); // 'native' (user permitted) | 'dom_render' (fallback) | 'none'
const hideFloatingBtn = ref(false);

const isDarkMode = computed(() => store.state?.darkMode || false);
const userEmail = computed(() => store.state?.user?.email || null);
const currentRouteName = computed(() => route?.name || route?.path || 'Dashboard');
const currentUrl = computed(() => typeof window !== 'undefined' ? window.location.href : '');

const captureWithNativeDisplayMedia = async () => {
  if (!navigator.mediaDevices?.getDisplayMedia) {
    throw new Error('getDisplayMedia not supported');
  }

  // Request screen/tab capture stream
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: {
      cursor: 'always',
      displaySurface: 'browser'
    },
    audio: false,
    preferCurrentTab: true
  });

  try {
    const video = document.createElement('video');
    video.autoplay = true;
    video.muted = true;
    video.playsInline = true;
    video.srcObject = stream;

    await new Promise((resolve, reject) => {
      video.onloadedmetadata = () => {
        video.play().then(resolve).catch(reject);
      };
      video.onerror = reject;
      setTimeout(() => reject(new Error('Video stream timeout')), 3000);
    });

    // Small delay to ensure the active frame is ready
    await new Promise(r => setTimeout(r, 100));

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || window.innerWidth;
    canvas.height = video.videoHeight || window.innerHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    return canvas.toDataURL('image/png', 0.9);
  } finally {
    // Always stop all tracks immediately
    stream.getTracks().forEach(track => track.stop());
  }
};

const captureWithHtml2Canvas = async () => {
  const scrollX = window.pageXOffset || document.documentElement.scrollLeft || 0;
  const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  const canvas = await html2canvas(document.documentElement, {
    useCORS: true,
    allowTaint: true,
    logging: false,
    scale: Math.min(window.devicePixelRatio || 1, 1.5),
    x: scrollX,
    y: scrollY,
    width: viewportWidth,
    height: viewportHeight,
    windowWidth: viewportWidth,
    windowHeight: viewportHeight,
    ignoreElements: (el) => {
      if (el.classList?.contains('error-report-modal-backdrop') ||
          el.tagName === 'IFRAME') {
        return true;
      }
      return false;
    }
  });

  return canvas.toDataURL('image/png', 0.85);
};

const openReportFlow = async () => {
  if (isCapturing.value || isSubmitting.value) return;

  isCapturing.value = true;
  comment.value = '';
  capturedScreenshot.value = null;
  captureMethod.value = 'none';

  // Blur any active element so no button/dropdown is left in focused state
  if (document.activeElement && typeof document.activeElement.blur === 'function') {
    document.activeElement.blur();
  }

  try {
    await nextTick();

    // 1. Try Native Browser Screenshot (getDisplayMedia)
    let screenshotData = null;
    try {
      screenshotData = await captureWithNativeDisplayMedia();
      if (screenshotData) {
        captureMethod.value = 'native'; // User permitted native screen capture
      }
    } catch (mediaErr) {
      // User cancelled permission, or not supported -> Fallback to html2canvas
      console.log('[ErrorReport] Native screen capture cancelled/unsupported, falling back to html2canvas:', mediaErr);
    }

    // 2. Fallback to html2canvas if native capture wasn't granted or completed
    if (!screenshotData) {
      screenshotData = await captureWithHtml2Canvas();
      if (screenshotData) {
        captureMethod.value = 'dom_render'; // Alternate DOM renderer fallback
      }
    }

    capturedScreenshot.value = screenshotData;
  } catch (err) {
    console.error('[ErrorReport] Screenshot capture failed:', err);
    toast.warning('Could not capture screen, but you can still submit your comment.', { duration: 4000 });
  } finally {
    isCapturing.value = false;
    isModalOpen.value = true;
  }
};

const closeModal = () => {
  if (isSubmitting.value) return;
  isModalOpen.value = false;
  showImagePreview.value = false;
  comment.value = '';
  capturedScreenshot.value = null;
  captureMethod.value = 'none';
};

const retakeScreenshot = async () => {
  isModalOpen.value = false;
  await nextTick();
  await openReportFlow();
};

const submitReport = async () => {
  isSubmitting.value = true;

  const payload = {
    comment: comment.value.trim(),
    screenshot: capturedScreenshot.value,
    capture_method: captureMethod.value,
    url: window.location.href,
    route_name: String(currentRouteName.value),
    screen_width: window.innerWidth,
    screen_height: window.innerHeight,
    user_agent: navigator.userAgent,
    app_mode: store.state?.appMode || 'production',
    api_mode: store.state?.apiMode || 'production',
  };

  try {
    const res = await http.post('/error-reports/submit', payload);
    const msg = res.data?.message || 'Error report sent to givesachin@gmail.com';
    toast.success(msg, { duration: 5000, position: 'top-right' });
    closeModal();
  } catch (error) {
    console.error('[ErrorReport] Submit failed:', error);
    const errMsg = error.response?.data?.message || error.message || 'Failed to send error report.';
    toast.error(errMsg, { duration: 6000, position: 'top-right' });
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <!-- Floating Button (Right Side Center, above modal zIndex) -->
  <button
    v-show="!hideFloatingBtn"
    id="errorReportFloatingBtn"
    type="button"
    class="error-report-floating-btn shadow-sm"
    :class="{ 'capturing': isCapturing }"
    :disabled="isCapturing"
    @click="openReportFlow"
    title="Report an error / issue"
    aria-label="Report an error"
  >
    <i v-if="!isCapturing" class="fas fa-bug"></i>
    <i v-else class="fas fa-spinner fa-spin"></i>
  </button>

  <!-- Error Report Modal (Higher z-index than any dialog) -->
  <teleport to="body">
    <div
      v-if="isModalOpen"
      class="error-report-modal-backdrop"
      @click.self="closeModal"
    >
      <div class="error-report-card shadow-lg" :class="{ 'dark-mode-card': isDarkMode }">
        <!-- Header -->
        <div class="error-report-header d-flex align-items-center justify-content-between p-3 border-bottom">
          <div class="d-flex align-items-center gap-2">
            <span class="error-report-icon-badge">
              <i class="fas fa-bug text-danger"></i>
            </span>
            <div>
              <h5 class="mb-0 font-weight-bold text-sm text-dark" :class="{ 'text-white': isDarkMode }">
                Report an Issue
              </h5>
            </div>
          </div>
          <button
            type="button"
            class="btn-close-custom"
            @click="closeModal"
            :disabled="isSubmitting"
            aria-label="Close"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="error-report-body p-3">

          <!-- Empty Comment Box -->
          <div class="mb-3">
            <label class="form-label text-xs font-weight-bold mb-1" :class="{ 'text-white': isDarkMode }">
              Describe what happened:
            </label>
            <textarea
              v-model="comment"
              class="form-control form-control-sm error-report-textarea"
              :class="{ 'dark-input': isDarkMode }"
              rows="3"
              placeholder="What were you trying to do, or what error occurred? (Optional)"
              autofocus
            ></textarea>
          </div>

          <!-- Screenshot Attachment Preview -->
          <div class="mb-2">
            <div class="d-flex align-items-center justify-content-between mb-1">
              <span class="text-xs font-weight-bold d-flex align-items-center gap-1" :class="{ 'text-white': isDarkMode }">
                <i class="fas fa-camera me-1 text-secondary"></i> Attached Screenshot
                <span v-if="captureMethod === 'native'" class="badge bg-success-subtle text-success text-xxxs px-1.5 py-0.5 rounded">
                  <i class="fas fa-check-circle me-1"></i> Browser Native
                </span>
                <span v-else-if="captureMethod === 'dom_render'" class="badge bg-secondary-subtle text-secondary text-xxxs px-1.5 py-0.5 rounded">
                  <i class="fas fa-draw-polygon me-1"></i> Standard Render
                </span>
              </span>
              <div class="d-flex gap-2">
                <button
                  v-if="capturedScreenshot"
                  type="button"
                  class="btn btn-link text-xxs text-primary p-0 m-0 text-decoration-none"
                  @click="showImagePreview = true"
                >
                  <i class="fas fa-expand me-1"></i> Preview Full
                </button>
                <button
                  type="button"
                  class="btn btn-link text-xxs text-secondary p-0 m-0 text-decoration-none"
                  @click="retakeScreenshot"
                  :disabled="isSubmitting"
                >
                  <i class="fas fa-sync-alt me-1"></i> Retake
                </button>
              </div>
            </div>

            <div v-if="capturedScreenshot" class="screenshot-preview-container border rounded" @click="showImagePreview = true">
              <img :src="capturedScreenshot" alt="Error Screenshot" class="screenshot-thumb" />
              <div class="preview-overlay">
                <span class="text-white text-xs font-weight-bold">
                  <i class="fas fa-search-plus me-1"></i> Click to Zoom
                </span>
              </div>
            </div>

            <div v-else class="p-3 text-center border rounded bg-light text-secondary text-xs">
              <i class="fas fa-image me-1"></i> No screenshot captured.
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="error-report-footer p-3 border-top d-flex justify-content-end gap-2">
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary mb-0 rounded-pill px-3"
            @click="closeModal"
            :disabled="isSubmitting"
          >
            Cancel
          </button>
          <button
            type="button"
            class="btn btn-sm btn-danger mb-0 rounded-pill px-4 d-flex align-items-center gap-1"
            @click="submitReport"
            :disabled="isSubmitting"
          >
            <i v-if="isSubmitting" class="fas fa-spinner fa-spin"></i>
            <i v-else class="fas fa-paper-plane"></i>
            <span>{{ isSubmitting ? 'Sending...' : 'Send Error Report' }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Fullscreen Image Lightbox Preview -->
    <div
      v-if="showImagePreview && capturedScreenshot"
      class="screenshot-lightbox"
      @click="showImagePreview = false"
    >
      <div class="lightbox-content" @click.stop>
        <button type="button" class="lightbox-close" @click="showImagePreview = false">
          <i class="fas fa-times"></i>
        </button>
        <img :src="capturedScreenshot" alt="Full Screenshot" class="lightbox-img" />
      </div>
    </div>
  </teleport>
</template>

<style scoped>
/* Floating Button: Right Side Center, Compact (like navbar buttons), Above modal zIndex */
.error-report-floating-btn {
  position: fixed;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 999999 !important; /* Visible above all high custom modal z-indices (up to 99999) */
  width: 34px;
  height: 34px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #f5365c 0%, #ea0606 100%);
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.25);
  border-right: none;
  border-radius: 8px 0 0 8px;
  cursor: pointer;
  box-shadow: -2px 4px 12px rgba(245, 54, 92, 0.35);
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
}

.error-report-floating-btn:hover {
  transform: translateY(-50%) translateX(-3px);
  box-shadow: -4px 6px 16px rgba(245, 54, 92, 0.5);
  background: linear-gradient(135deg, #f75676 0%, #ea0606 100%);
}

.error-report-floating-btn:active {
  transform: translateY(-50%) translateX(-1px);
}

.error-report-floating-btn.capturing {
  opacity: 0.85;
  cursor: wait;
}

/* Modal Backdrop */
.error-report-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.65);
  backdrop-filter: blur(4px);
  z-index: 1000000 !important; /* Above floating button and any modal dialogs */
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  animation: fadeIn 0.15s ease-out;
}

/* Card Container */
.error-report-card {
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  background: #ffffff;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  animation: slideUp 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.dark-mode-card {
  background: #1e293b !important;
  color: #f8fafc !important;
  border-color: #334155 !important;
}

.dark-input {
  background-color: #0f172a !important;
  border-color: #334155 !important;
  color: #f8fafc !important;
}

.error-report-icon-badge {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: rgba(245, 54, 92, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
}

.btn-close-custom {
  background: transparent;
  border: none;
  font-size: 14px;
  color: #94a3b8;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;
  transition: all 0.15s ease;
}

.btn-close-custom:hover {
  color: #0f172a;
  background: rgba(0, 0, 0, 0.05);
}

.error-report-textarea {
  resize: vertical;
  min-height: 75px;
  border-radius: 8px;
  font-size: 13px;
}

/* Screenshot Preview */
.screenshot-preview-container {
  position: relative;
  width: 100%;
  height: 140px;
  overflow: hidden;
  background: #0f172a;
  cursor: pointer;
}

.screenshot-thumb {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  opacity: 0.95;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.screenshot-preview-container:hover .screenshot-thumb {
  transform: scale(1.02);
  opacity: 0.8;
}

.preview-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  opacity: 0;
  transition: opacity 0.2s ease;
}

.screenshot-preview-container:hover .preview-overlay {
  opacity: 1;
}

/* Lightbox Modal */
.screenshot-lightbox {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 1000010 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.lightbox-content {
  position: relative;
  max-width: 92vw;
  max-height: 92vh;
}

.lightbox-close {
  position: absolute;
  top: -14px;
  right: -14px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #ffffff;
  color: #0f172a;
  border: none;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.3);
  cursor: pointer;
  z-index: 10;
}

.lightbox-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(12px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}
</style>
